import { SlateNode } from '@all-hands-hq/plex-graphql-schema';
import React, { useEffect } from 'react';

import {
  CommentType,
  FullCommentFragment,
  GetCommentsSubscriptionSubscription,
  GetCommentsSubscriptionSubscriptionVariables,
  GetQuestionCommentsQuery,
  QuestionDetailsFragment,
  useCreateQuestionCommentMutation,
  useGetQuestionCommentsQuery,
} from '../../../../graphql/gen/types';
import { GET_COMMENTS_SUBSCRIPTION } from '../../../../graphql/subscriptions/GetComments';
import { canCreateAnswer, canCreateFact } from '../../permissions';
import { logAnalytics } from '../../shared/logAnalytics';
import { Comments } from '../Comments';

type Props = {
  question: QuestionDetailsFragment;
  commentsVisible: boolean;
  onChangeCommentsVisibility: (visible: boolean) => void;
};

export function QuestionComments({ question, commentsVisible, onChangeCommentsVisibility }: Props): React.ReactElement {
  const [createCommentMutation, { loading: commentMutationLoading }] = useCreateQuestionCommentMutation();
  const questionId = question.id;
  const { loading, data, subscribeToMore } = useGetQuestionCommentsQuery({ variables: { questionId } });
  const plexRelation = question.plex.currentUserRelation.state;

  useEffect(() => {
    const unsubscribe = subscribeToMore<
      GetCommentsSubscriptionSubscription,
      GetCommentsSubscriptionSubscriptionVariables
    >({
      document: GET_COMMENTS_SUBSCRIPTION,
      variables: { id: questionId, isPost: false },
      updateQuery: (prev: GetQuestionCommentsQuery, { subscriptionData }): GetQuestionCommentsQuery => {
        const comment: FullCommentFragment | null | undefined = subscriptionData.data.comment;
        if (!prev.loggedIn || !prev.loggedIn.questionById || !comment) {
          return prev;
        }
        return {
          ...prev,
          loggedIn: {
            ...prev.loggedIn,
            questionById: {
              ...prev.loggedIn.questionById,
              comments: [
                comment,
                ...prev.loggedIn.questionById.comments.filter((item) => item.id !== comment.id),
              ].filter((comment) => !comment.deleted),
            },
          },
        };
      },
    });

    return unsubscribe;
  }, [questionId, subscribeToMore]);

  const onCreateComment = ({ body, type }: { body: SlateNode[]; type: CommentType }): Promise<unknown> => {
    return createCommentMutation({
      variables: {
        comment: { body, type, questionId: question.id },
      },
    }).then(() => logAnalytics({ name: 'comment added', details: { plexId: question.plex.id, itemId: question.id } }));
  };

  return (
    <Comments
      loading={loading}
      replyLoading={commentMutationLoading}
      comments={data?.loggedIn?.questionById?.comments}
      onCreateComment={onCreateComment}
      commentsVisible={commentsVisible}
      onChangeCommentsVisibility={onChangeCommentsVisibility}
      showPromoteComment={canCreateAnswer({ plexRelation }) && canCreateFact(plexRelation)}
    />
  );
}
